import React, { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Button, Card, Container, Form, Row, Col } from 'react-bootstrap';
import { BiPlusCircle, BiTrash } from 'react-icons/bi';
import {
  useCreateAdditionalService,
  useGetSelectAdditionalServices,
} from '../../apiEndpoints/exhibitorApiEndpoints';
import { useNavigate } from 'react-router-dom';

interface FormData {
  services: {
    service_id: string | number;
    quantity: number;
    price: number;
  }[];
}

const AddAdditionalService = ({
  handleClose,
}: {
  handleClose?: () => void;
}) => {
  const navigate = useNavigate();
  const { data } = useGetSelectAdditionalServices();
  const { mutate, isSuccess, data: createData } = useCreateAdditionalService();
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      services: [{ service_id: '', quantity: 1, price: 0 }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'services',
  });

  const onSubmit = (data: FormData) => {
    const services = data?.services?.map((item) => ({
      quantity: item.quantity,
      service_id: Number(item.service_id),
    }));

    mutate({ services });
  };

  // Handle service selection to update the price
  const handleServiceChange = (index: number, service_id: string) => {
    const selectedService = data?.data?.find(
      (service) => service.id === Number(service_id)
    );
    if (selectedService) {
      setValue(`services.${index}.price`, Number(selectedService.amount));
    }
  };

  // Calculate total
  const calculateTotal = () => {
    const services = watch('services');
    return services?.reduce((total, item) => {
      return total + item.quantity * item.price;
    }, 0);
  };

  useEffect(() => {
    if (isSuccess && createData?.data?.invoice_id) {
      handleClose?.();
      navigate(`/profile/view-billing-info/${createData?.data?.invoice_id}`);
    }
  }, [isSuccess, createData?.data?.invoice_id]);
  return (
    <Container className='py-4'>
      <Card className='shadow-sm'>
        <Card.Body>
          <Form onSubmit={handleSubmit(onSubmit)}>
            {fields.map((item, index) => (
              <div key={item.id} className='mb-4 p-3 border rounded'>
                <Row className='mb-3 align-items-end'>
                  <Col md={4}>
                    <Form.Group controlId={`service-${index}`}>
                      <Form.Label>Service</Form.Label>
                      <Form.Select
                        isInvalid={!!errors.services?.[index]?.service_id}
                        {...register(`services.${index}.service_id`, {
                          required: 'Service is required',
                          onChange: (e) =>
                            handleServiceChange(index, e.target.value),
                        })}
                      >
                        <option value=''>Select a service</option>
                        {data?.data?.map((service) => (
                          <option
                            key={service.id}
                            disabled={!service.status}
                            value={service.id}
                          >
                            {service.name} ({service?.amount})
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type='invalid'>
                        {errors.services?.[index]?.service_id?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group controlId={`quantity-${index}`}>
                      <Form.Label>Quantity</Form.Label>
                      <Form.Control
                        type='number'
                        min='1'
                        isInvalid={!!errors.services?.[index]?.quantity}
                        {...register(`services.${index}.quantity`, {
                          required: 'Quantity is required',
                          min: { value: 1, message: 'Minimum quantity is 1' },
                          valueAsNumber: true,
                        })}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.services?.[index]?.quantity?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group controlId={`price-${index}`}>
                      <Form.Label>Unit Price</Form.Label>
                      <Form.Control
                        type='number'
                        step='0.01'
                        min='0'
                        readOnly
                        isInvalid={!!errors.services?.[index]?.price}
                        {...register(`services.${index}.price`, {
                          required: 'Price is required',
                          min: {
                            value: 0,
                            message: 'Price cannot be negative',
                          },
                          valueAsNumber: true,
                        })}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group controlId={`total-${index}`}>
                      <Form.Label>Total</Form.Label>
                      <Form.Control
                        type='number'
                        step='0.01'
                        min='0'
                        readOnly
                        value={(
                          watch(`services.${index}.quantity`) *
                          watch(`services.${index}.price`)
                        ).toFixed(2)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2} className='text-end'>
                    <div className='d-flex justify-content-end gap-2'>
                      {index > 0 ? (
                        <Button
                          variant='outline-danger'
                          // size='sm'
                          onClick={() => remove(index)}
                          title='Remove service'
                          className='text-center'
                        >
                          <BiTrash size={20} />
                        </Button>
                      ) : (
                        <div style={{ width: '38px' }}></div>
                      )}
                      <Button
                        variant='outline-primary'
                        onClick={() =>
                          append({ service_id: '', quantity: 1, price: 0 })
                        }
                        size='sm'
                        title='Add service'
                      >
                        <BiPlusCircle size={20} />
                      </Button>
                    </div>
                  </Col>
                </Row>
              </div>
            ))}

            <div className='p-3 bg-light rounded mb-4'>
              <Row>
                <Col className='text-end'>
                  <h5 className='mb-0'>
                    Grand Total: <strong>{calculateTotal().toFixed(2)}</strong>
                  </h5>
                </Col>
              </Row>
            </div>

            <div>
              <Button
                className='submit-button common-btn'
                variant='primary'
                type='submit'
                size='lg'
              >
                Submit
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AddAdditionalService;
